import React from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../App";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { X, Plus, Minus, Trash2, ShoppingBag, Store, AlertCircle } from "lucide-react";

const CartSidebar = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { cart, updateCartItem, clearCart, loading } = useCart();
  const { isMember, isAuthenticated } = useAuth();

  const handleQuantityChange = (item, delta) => {
    const newQuantity = item.quantity + delta;
    // Use retailer for live products, fallback to store_id for old format
    const storeId = item.retailer || item.store_id;
    updateCartItem(item.product_id, storeId, newQuantity);
  };

  const handleRemoveItem = (item) => {
    // Use retailer for live products, fallback to store_id for old format
    const storeId = item.retailer || item.store_id;
    updateCartItem(item.product_id, storeId, 0);
  };

  const handleCheckout = () => {
    onClose();
    navigate("/checkout");
  };

  const handleViewCart = () => {
    onClose();
    navigate("/cart");
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col" data-testid="cart-sidebar">
        <SheetHeader className="border-b pb-4">
          <SheetTitle className="flex items-center gap-2 font-['Outfit'] text-xl">
            <ShoppingBag className="w-5 h-5" />
            Your Cart
            {cart.items?.length > 0 && (
              <span className="text-sm font-normal text-slate-500">
                ({cart.items.length} items)
              </span>
            )}
          </SheetTitle>
        </SheetHeader>

        {cart.items?.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center py-12">
            <ShoppingBag className="w-16 h-16 text-slate-300 mb-4" />
            <p className="text-slate-600 font-medium">Your cart is empty</p>
            <p className="text-sm text-slate-400 mt-1">Start adding items to compare prices!</p>
            <Button
              className="mt-6"
              onClick={() => {
                onClose();
                navigate("/products");
              }}
              data-testid="browse-products-btn"
            >
              Browse Products
            </Button>
          </div>
        ) : (
          <>
            {/* Store count info */}
            {cart.store_count > 1 && (
              <div className="flex items-center gap-2 p-3 bg-sky-50 rounded-lg mt-4 text-sm">
                <Store className="w-4 h-4 text-sky-600" />
                <span className="text-slate-700">
                  This order will be fulfilled from <strong>{cart.store_count} stores</strong>
                </span>
              </div>
            )}

            {/* Guest fee notice */}
            {!isAuthenticated && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg mt-3 text-sm">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-amber-800 font-medium">Guest checkout: 7% fee</p>
                  <p className="text-amber-700 text-xs"><button onClick={() => { onClose(); navigate("/?auth=signup"); }} className="text-sky-600 hover:text-sky-700 font-semibold underline" data-testid="signup-link-sidebar">Sign up</button> for member pricing (4% fee)</p>
                </div>
              </div>
            )}

            <ScrollArea className="flex-1 py-4">
              <div className="space-y-4">
                {cart.items?.map((item, index) => (
                  <div
                    key={`${item.product_id}-${item.retailer || item.store_id}`}
                    className="flex gap-3 p-3 rounded-lg border bg-white"
                    data-testid={`cart-item-${index}`}
                  >
                    <img
                      src={item.image_url || "https://images.unsplash.com/photo-1542838132-92c53300491e?w=80&h=80&fit=crop"}
                      alt={item.product_name}
                      className="w-16 h-16 rounded-lg object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://images.unsplash.com/photo-1542838132-92c53300491e?w=80&h=80&fit=crop";
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-sm text-slate-900 truncate">
                        {item.product_name}
                      </h4>
                      <p className="text-xs text-slate-500 truncate">{item.store_name}</p>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-slate-900">
                            ${item.price.toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center border rounded-lg">
                          <button
                            onClick={() => handleQuantityChange(item, -1)}
                            className="p-1.5 hover:bg-slate-100 rounded-l-lg"
                            disabled={item.quantity <= 1}
                            data-testid={`decrease-qty-${index}`}
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="px-3 text-sm font-medium">{item.quantity}</span>
                          <button
                            onClick={() => handleQuantityChange(item, 1)}
                            className="p-1.5 hover:bg-slate-100 rounded-r-lg"
                            data-testid={`increase-qty-${index}`}
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        <button
                          onClick={() => handleRemoveItem(item)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
                          data-testid={`remove-item-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            <Separator />

            {/* Totals */}
            <div className="py-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Subtotal</span>
                <span className="font-medium">${cart.subtotal?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">
                  Convenience Fee ({isMember ? "4%" : "7%"})
                </span>
                <span className="font-medium">${cart.convenience_fee?.toFixed(2)}</span>
              </div>
              <Separator />
              <div className="flex justify-between">
                <span className="font-semibold">Estimated Total</span>
                <span className="font-bold text-lg">${cart.total?.toFixed(2)}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-2">
              <Button
                className="w-full btn-accent h-11"
                onClick={handleCheckout}
                data-testid="checkout-btn"
              >
                Proceed to Checkout
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={handleViewCart}
                data-testid="view-cart-btn"
              >
                View Full Cart
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartSidebar;
